// A lightweight renderer for Atlassian Document Format (ADF)
// Supports common nodes used in Jira: paragraph, text (with marks), bullet/ordered lists, list items, headings, hardBreak, blockquote
// Extend as needed when new node types appear in payloads.

function DocRenderer({ doc, className = '' }) {
  if (!doc || typeof doc !== 'object' || doc.type !== 'doc' || !Array.isArray(doc.content)) {
    return null;
  }

  const applyMarks = (marks, children) => {
    if (!Array.isArray(marks) || marks.length === 0) {
      return children;
    }

    // Apply marks inside-out in order
    return marks.reduce((acc, mark, idx) => {
      if (!mark || typeof mark !== 'object') return acc;
      switch (mark.type) {
        case 'strong':
          return <strong key={`mk-strong-${idx}`}>{acc}</strong>;
        case 'em':
          return <em key={`mk-em-${idx}`}>{acc}</em>;
        case 'underline':
          return <u key={`mk-u-${idx}`}>{acc}</u>;
        case 'strike':
          return <s key={`mk-s-${idx}`}>{acc}</s>;
        case 'code':
          return <code key={`mk-code-${idx}`} className="px-1 py-0.5 rounded bg-gray-100">{acc}</code>;
        case 'link':
          // ADF link mark typically has attrs.href
          // Fallback to span if missing
          if (mark.attrs && mark.attrs.href) {
            return (
              <a
                key={`mk-link-${idx}`}
                href={mark.attrs.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                {acc}
              </a>
            );
          }
          return <span key={`mk-link-${idx}`}>{acc}</span>;
        default:
          return acc;
      }
    }, children);
  };

  const renderNodes = (nodes) => {
    if (!Array.isArray(nodes)) return null;
    return nodes.map((node, index) => renderNode(node, index));
  };

  const renderNode = (node, index) => {
    if (!node || typeof node !== 'object') return null;
    const key = `n-${node?.type}-${index}`;

    switch (node.type) {
      case 'paragraph': {
        return (
          <p key={key} className="mb-2">
            {renderNodes(node.content)}
          </p>
        );
      }
      case 'text': {
        const text = node.text || '';
        return (
          <>{applyMarks(node.marks, text)}</>
        );
      }
      case 'hardBreak': {
        return <br key={key} />;
      }
      case 'bulletList': {
        return (
          <ul key={key} className="list-disc ml-6 my-2">
            {renderNodes(node.content)}
          </ul>
        );
      }
      case 'orderedList': {
        return (
          <ol key={key} className="list-decimal ml-6 my-2">
            {renderNodes(node.content)}
          </ol>
        );
      }
      case 'listItem': {
        return (
          <li key={key}>
            {renderNodes(node.content)}
          </li>
        );
      }
      case 'heading': {
        const level = Math.min(Math.max(node.attrs?.level || 1, 1), 6);
        const HeadingTag = `h${level}`;
        const classByLevel = {
          1: 'text-2xl font-semibold',
          2: 'text-xl font-semibold',
          3: 'text-lg font-semibold',
          4: 'text-base font-semibold',
          5: 'text-sm font-semibold',
          6: 'text-xs font-semibold',
        }[level];
        return (
          <HeadingTag key={key} className={`${classByLevel} mt-2 mb-1`}>
            {renderNodes(node.content)}
          </HeadingTag>
        );
      }
      case 'blockquote': {
        return (
          <blockquote key={key} className="border-l-4 border-gray-300 pl-4 italic text-gray-700 my-2">
            {renderNodes(node.content)}
          </blockquote>
        );
      }
      // Media nodes can appear in comments. We skip them here; they are handled elsewhere in the UI.
      case 'mediaSingle':
      case 'media': {
        return (
          <div key={key} className="my-2 text-gray-500 text-sm">
            Media content
          </div>
        );
      }
      default: {
        // Fallback: render children if any
        if (Array.isArray(node.content)) {
          return <div key={key}>{renderNodes(node.content)}</div>;
        }
        return null;
      }
    }
  };

  return (
    <div className={className}>
      {renderNodes(doc.content)}
    </div>
  );
}

export default DocRenderer;


